import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  getSecuritySummary, getLoginHeatmap, getSecurityEvents,
  getActiveLockouts, unlockAccount, getSecurityAnomalies, getTopIPs
} from '../lib/api';
import {
  Shield, ShieldAlert, Lock, Unlock, Activity, Users, Key, Clock,
  AlertTriangle, CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';

const RANGES = [
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '60d', label: 'Last 60 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '180d', label: 'Last 180 Days' },
];

const DAY_NAMES = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EVENT_COLORS = {
  login: 'bg-emerald-100 text-emerald-800',
  login_success: 'bg-emerald-100 text-emerald-800',
  auth_login: 'bg-emerald-100 text-emerald-800',
  login_failure: 'bg-red-100 text-red-800',
  auth_login_failed: 'bg-red-100 text-red-800',
  login_geo_blocked: 'bg-orange-100 text-orange-800',
  account_locked: 'bg-red-200 text-red-900',
  password_change_post: 'bg-blue-100 text-blue-800',
  auth_password_change: 'bg-blue-100 text-blue-800',
  auth_password_reset: 'bg-blue-100 text-blue-800',
  auth_mfa_setup: 'bg-violet-100 text-violet-800',
  auth_mfa_verify: 'bg-violet-100 text-violet-800',
  mfa_enabled: 'bg-emerald-100 text-emerald-800',
  mfa_disabled: 'bg-amber-100 text-amber-800',
  reset_mfa: 'bg-amber-100 text-amber-800',
  auth_create: 'bg-sky-100 text-sky-800',
  user_management_post: 'bg-sky-100 text-sky-800',
  api_key_created: 'bg-indigo-100 text-indigo-800',
  api_key_revoked: 'bg-orange-100 text-orange-800',
  settings_change_put: 'bg-slate-100 text-slate-800',
  backups_create: 'bg-teal-100 text-teal-800',
};

function MetricCard({ title, value, icon: Icon, color = 'text-muted-foreground', sub }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <Icon className={`w-8 h-8 ${color} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}

function HeatmapGrid({ data }) {
  if (!data) return null;
  const combined = {};
  (data.success || []).forEach(d => { combined[`${d.day}-${d.hour}`] = { s: d.count, f: 0 }; });
  (data.failed || []).forEach(d => {
    const k = `${d.day}-${d.hour}`;
    if (!combined[k]) combined[k] = { s: 0, f: 0 };
    combined[k].f = d.count;
  });

  const maxVal = Math.max(1, ...Object.values(combined).map(v => v.s + v.f));

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <div className="flex gap-0.5 mb-1 pl-10">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="w-5 text-center text-[9px] text-muted-foreground">{h}</div>
          ))}
        </div>
        {[1, 2, 3, 4, 5, 6, 7].map(day => (
          <div key={day} className="flex items-center gap-0.5 mb-0.5">
            <div className="w-9 text-right text-[10px] text-muted-foreground pr-1">{DAY_NAMES[day]}</div>
            {Array.from({ length: 24 }, (_, hour) => {
              const cell = combined[`${day}-${hour}`];
              const total = cell ? cell.s + cell.f : 0;
              const intensity = total > 0 ? Math.min(1, total / maxVal) : 0;
              const hasFail = cell && cell.f > 0;
              const bg = total === 0
                ? 'bg-muted/30'
                : hasFail
                  ? `bg-red-${Math.ceil(intensity * 5) * 100}`
                  : `bg-emerald-${Math.ceil(intensity * 5) * 100}`;
              return (
                <div
                  key={hour}
                  className={`w-5 h-5 rounded-sm ${bg} border border-background`}
                  title={`${DAY_NAMES[day]} ${hour}:00, ${cell?.s || 0} success, ${cell?.f || 0} failed`}
                  style={{
                    backgroundColor: total === 0 ? undefined
                      : hasFail ? `rgba(239,68,68,${0.15 + intensity * 0.75})`
                      : `rgba(16,185,129,${0.15 + intensity * 0.75})`
                  }}
                />
              );
            })}
          </div>
        ))}
        <div className="flex items-center gap-4 mt-2 pl-10 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(16,185,129,0.5)' }} /> Successful</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'rgba(239,68,68,0.5)' }} /> Failed</span>
        </div>
      </div>
    </div>
  );
}

export default function SecurityAuditDashboard() {
  const [range, setRange] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [events, setEvents] = useState({ events: [], total: 0, page: 1, pages: 1 });
  const [eventPage, setEventPage] = useState(1);
  const [eventFilter, setEventFilter] = useState('');
  const [lockouts, setLockouts] = useState([]);
  const [topIPs, setTopIPs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, heatRes, evtRes, lockRes, ipRes] = await Promise.all([
        getSecuritySummary(range),
        getLoginHeatmap(range),
        getSecurityEvents(range, eventFilter || undefined, eventPage),
        getActiveLockouts(),
        getTopIPs(range),
      ]);
      setSummary(sumRes.data);
      setHeatmap(heatRes.data);
      setEvents(evtRes.data);
      setLockouts(lockRes.data.lockouts || []);
      setTopIPs(ipRes.data.ips || []);
    } catch {
      toast.error('Failed to load security data');
    } finally {
      setLoading(false);
    }
  }, [range, eventPage, eventFilter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleUnlock = async (email) => {
    try {
      await unlockAccount(email);
      toast.success(`Unlocked ${email}`);
      loadAll();
    } catch {
      toast.error('Failed to unlock account');
    }
  };

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="security-audit-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Security Audit Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time security monitoring and anomaly detection</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={range} onValueChange={(v) => { setRange(v); setEventPage(1); }}>
            <SelectTrigger className="w-44" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAll} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="summary-cards">
          <MetricCard title="Total Logins" value={summary.total_logins} icon={CheckCircle} color="text-emerald-600" />
          <MetricCard title="Failed Logins" value={summary.failed_logins} icon={XCircle} color="text-red-500" />
          <MetricCard title="Lockouts" value={summary.lockouts} icon={Lock} color="text-orange-500" sub={`${summary.active_lockouts} active`} />
          <MetricCard title="Unique IPs" value={summary.unique_ips} icon={Activity} color="text-blue-500" />
          <MetricCard title="MFA Changes" value={summary.mfa_changes} icon={Key} color="text-violet-500" />
          <MetricCard title="Pwd Changes" value={summary.password_changes} icon={ShieldAlert} color="text-amber-500" />
        </div>
      )}

      {/* Heatmap + Active Lockouts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Login Activity Heatmap</CardTitle>
            <CardDescription>Login attempts by day of week and hour (UTC)</CardDescription>
          </CardHeader>
          <CardContent>
            <HeatmapGrid data={heatmap} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-red-500" />
              Active Lockouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lockouts.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No accounts locked</p>
            ) : (
              <div className="space-y-2">
                {lockouts.map((l, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                    <div>
                      <p className="font-mono text-xs">{l.email}</p>
                      <p className="text-xs text-muted-foreground">{l.count} attempts</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleUnlock(l.email)} data-testid={`unlock-${l.email}`}>
                      <Unlock className="w-3 h-3 mr-1" /> Unlock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top IPs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Top IP Addresses</CardTitle>
          <CardDescription>Most active IPs by login activity</CardDescription>
        </CardHeader>
        <CardContent>
          {topIPs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No IP data available</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-4">IP Address</th>
                    <th className="pb-2 pr-4">Total</th>
                    <th className="pb-2 pr-4">Success</th>
                    <th className="pb-2 pr-4">Failed</th>
                    <th className="pb-2 pr-4">Users</th>
                    <th className="pb-2">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {topIPs.map((ip, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1.5 pr-4 font-mono text-xs">{ip.ip}</td>
                      <td className="py-1.5 pr-4">{ip.total}</td>
                      <td className="py-1.5 pr-4 text-emerald-600">{ip.successes}</td>
                      <td className="py-1.5 pr-4 text-red-500">{ip.failures}</td>
                      <td className="py-1.5 pr-4">
                        <div className="flex flex-wrap gap-1">
                          {ip.users.map((u, j) => (
                            <span key={j} className="text-xs bg-muted px-1.5 py-0.5 rounded">{u}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-1.5 text-xs text-muted-foreground">{new Date(ip.last_seen).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Event Timeline */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Security Event Timeline</CardTitle>
              <CardDescription>{events.total} events found</CardDescription>
            </div>
            <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v === 'all' ? '' : v); setEventPage(1); }}>
              <SelectTrigger className="w-48" data-testid="event-filter">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="login_success">Logins</SelectItem>
                <SelectItem value="login_failure">Failed Logins</SelectItem>
                <SelectItem value="account_locked">Lockouts</SelectItem>
                <SelectItem value="password_change_post">Password Changes</SelectItem>
                <SelectItem value="auth_create">User Created</SelectItem>
                <SelectItem value="settings_change_put">Settings Changes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {events.events.map((evt, i) => (
              <div key={i} className="flex items-start gap-3 p-2 rounded hover:bg-muted/50 text-sm" data-testid={`event-row-${i}`}>
                <Badge variant="secondary" className={`text-[10px] shrink-0 ${EVENT_COLORS[evt.action] || 'bg-gray-100'}`}>
                  {evt.action?.replace(/_/g, ' ')}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-xs truncate">{evt.description || evt.action}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {evt.user_email || 'system'} {evt.ip_address ? `from ${evt.ip_address}` : ''}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(evt.timestamp).toLocaleString()}
                </span>
              </div>
            ))}
            {events.events.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-6">No security events in this time range</p>
            )}
          </div>
          {events.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
              <Button
                variant="outline" size="sm"
                onClick={() => setEventPage(p => Math.max(1, p - 1))}
                disabled={eventPage <= 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {events.page} of {events.pages}
              </span>
              <Button
                variant="outline" size="sm"
                onClick={() => setEventPage(p => Math.min(events.pages, p + 1))}
                disabled={eventPage >= events.pages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
